<template>
  <block-item-card
    ref="loansMaturity"
    :title="$t('ALM.Section.LoansPortfolio.LoansMaturity.Title')"
    :tooltip="$t('ALM.Section.LoansPortfolio.LoansMaturity.InfoTip')"
    class="fill-height d-flex flex-column"
  >
    <template #actions>
      <v-spacer />
      <div class="d-flex align-center ml-4">
        <div class="d-flex align-center mr-4">
          <div
            class="mr-2"
            style="width: 12px; height: 12px; background: #4CAF50; border-radius: 100%;"
          />
          <span class="text-size-12 synthesis-ui-grey-01--text">Actual Maturity</span>
        </div>
        <div class="d-flex align-center">
          <div
            class="mr-2"
            :style="`width: 12px; height: 12px; background: ${$vuetify.theme.themes.light['synthesis-ui-yellow-03']}; border-radius: 100%;`"
          />
          <span class="text-size-12 synthesis-ui-grey-01--text">Repricing Period</span>
        </div>
      </div>
    </template>

    <v-lazy
      :options="{threshold: .5}"
      transition="fade-transition"
    >
      <e-chart
        :ref="`echart${chartRefId}`"
        :autoresize="true"
        :option="chartOpts"
        :style="`height: 400px;`"
        class="chart"
      />
    </v-lazy>
  </block-item-card>
</template>
<script>
import BlockItemCard      from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors   from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta                     from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency   from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import baseEChartBarProps from '@/theme/default/components/charts/echarts/mixins/bar/baseEChartBarProps'
import baseEChartBarData  from '@/theme/default/components/charts/echarts/mixins/bar/baseEChartBarData'
import chartsColorMixin   from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'

import { toNumberFormatNotationShort } from '@/lib/utils/helper'

import { use }                 from 'echarts/core'
import { BarChart }            from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer }      from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'

use([
  BarChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'ALMLoansMaturity',
  components: { BlockItemCard },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, baseEChartBarProps, baseEChartBarData, chartsColorMixin, riskAnalysisId],
  props     : {
    chartDataOptions: {
      type   : Array,
      default: () => []
    }
  },
  dataStore: {
    appToolbarExtended: 'App.Config.AppToolbarExtended',

    Loans: 'ALM.Loans.Data',

    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      breakpoint: (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6)
    }
  },
  computed: {
    loansMaturity () {
      return this.Loans.LoansMaturity || null
    },

    chartOpts () {
      if (!this.loansMaturity) {
        return {}
      }

      const xAxis = this.loansMaturity.labels
      const actualMaturityData = this.loansMaturity.maturityData.map(item => item.actualMaturity)
      const repricingMaturityData = this.loansMaturity.maturityData.map(item => item.repricingMaturity)

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : {
            color: '#FFFFFF'
          },
          formatter: (params) => {
            let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`
            params.forEach(param => {
              const value = toNumberFormatNotationShort(param.value) || 0
              result += `<div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; margin-right: 8px;"></span>
                <span>${param.seriesName}: ${this.riskBaseCurrencyDisplayName}${value}</span>
              </div>`
            })
            return result
          }
        },
        legend: {
          show: false
        },
        grid: {
          left        : 15,
          top         : 40,
          right       : 15,
          bottom      : 20,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : xAxis,
          axisLabel: {
            show     : true,
            color    : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize : 12,
            formatter: (value) => {
              // Keep labels on the same line, no wrapping
              return value
            },
            interval: 0
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: false }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            show: false
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        series: [
          {
            name     : 'Actual Maturity',
            type     : 'bar',
            data     : actualMaturityData,
            itemStyle: {
              color       : this.themeColorRGBA('synthesis-ui-green-02'),
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show      : true,
              position  : 'top',
              color     : this.themeColorRGBA('synthesis-ui-grey-01'),
              fontSize  : 11,
              fontWeight: 'normal',
              formatter : (params) => {
                return this.riskBaseCurrencyDisplayName + (toNumberFormatNotationShort(params.value) || 0)
              }
            },
            barWidth       : '35%',
            showBackground : true,
            backgroundStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06')
            }
          },
          {
            name     : 'Repricing Period',
            type     : 'bar',
            data     : repricingMaturityData,
            itemStyle: {
              color       : this.themeColorRGBA('synthesis-ui-yellow-03'),
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show      : true,
              position  : 'top',
              color     : this.themeColorRGBA('synthesis-ui-grey-01'),
              fontSize  : 11,
              fontWeight: 'normal',
              formatter : (params) => {
                return this.riskBaseCurrencyDisplayName + (toNumberFormatNotationShort(params.value) || 0)
              }
            },
            barWidth       : '35%',
            barGap         : '10%',
            showBackground : true,
            backgroundStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06')
            }
          }
        ]
      }
    }
  },
  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }

  },
  created () {
  },
  beforeMount () {},
  mounted () {
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {

  }
}
    </script>

    <style scoped>
.chart {
  width: 100%;
}
    </style>
